import React, { useState, useEffect } from 'react';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ScatterChart, Scatter, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ComposedChart, Area } from 'recharts';
import { Filter, Clock, Calendar, Zap, Brain, Target, ArrowUp, ArrowDown, ChevronDown, Users, BarChart2, BookOpen, ZapIcon, FileText, PenTool, ChartBar, AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../supabase/client';
import { useNavigate } from 'react-router-dom';

const NotEnoughTestsView = ({ testsCompleted, currentScore, totalTests }: { currentScore: number; testsCompleted: number, totalTests: number }) => {
  const navigate = useNavigate();

  return (
    <div className="bg-white rounded-lg shadow p-8 text-center">
      <div className="flex justify-center mb-6">
        <svg width="200" height="200" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
          {/* Clipboard with charts */}
          <rect x="40" y="15" width="120" height="170" rx="6" fill="#F3F4F6" stroke="#D1D5DB" strokeWidth="2" />
          <rect x="50" y="25" width="100" height="150" rx="2" fill="white" stroke="#D1D5DB" strokeWidth="2" />
          
          {/* Clipboard top */}
          <rect x="75" y="5" width="50" height="20" rx="6" fill="#3B82F6" stroke="#2563EB" strokeWidth="2" />
          
          {/* Progress circles */}
          <circle cx="75" cy="70" r="20" fill="#DBEAFE" stroke="#3B82F6" strokeWidth="2" />
          <circle cx="125" cy="70" r="20" fill="#DBEAFE" stroke="#3B82F6" strokeWidth="2" />
          <circle cx="100" cy="120" r="20" fill="#DBEAFE" stroke="#3B82F6" strokeWidth="2" />
          
          {/* Check marks for completed tests */}
          {testsCompleted >= 1 && (
            <path d="M65,70 L72,77 L85,64" fill="none" stroke="#3B82F6" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          )}
          {testsCompleted >= 2 && (
            <path d="M115,70 L122,77 L135,64" fill="none" stroke="#3B82F6" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
          )}
          
          {/* Progress bar at bottom */}
          <rect x="60" y="150" width="80" height="10" rx="5" fill="#DBEAFE" />
          <rect x="60" y="150" width={testsCompleted * 26.7} height="10" rx="5" fill="#3B82F6" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-gray-800 mb-3">
        {testsCompleted === 1 ? "Great start!" : "You're making progress!"}
      </h2>
      <p className="text-gray-600 mb-6 max-w-md mx-auto">
        You've completed {testsCompleted} {testsCompleted === 1 ? 'test' : 'tests'} so far. 
        Complete at least 3 tests to unlock detailed performance analytics and personalized 
        insights to optimize your LSAT preparation strategy.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-1 gap-4 max-w-md mx-auto justify-center align-center content-center place-items-center">
        <a 
          onClick={(e) => { e.preventDefault(); navigate('/order') }}
          href="/dashboard"
          className="px-6 py-3 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium flex items-center justify-center hover:opacity-90 transition-opacity shadow"
        >
          Take Another Test
        </a>
        {/* <button 
          onClick={() => {}}
          className="px-6 py-3 rounded-lg border border-indigo-600 text-indigo-600 font-medium flex items-center justify-center hover:bg-indigo-50 transition-colors"
        >
          View Basic Results
        </button> */}
      </div>
      
      {/* Current score stats if available */}
      {currentScore > 0 && (
        <div className="mt-8 p-6 bg-blue-50 rounded-lg max-w-md mx-auto">
          <h3 className="font-bold text-gray-800 mb-2">Current Progress</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="text-gray-500 text-sm">Current Score</p>
              <p className="text-2xl font-bold text-blue-600">{currentScore}</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <p className="text-gray-500 text-sm">Tests Completed</p>
              <p className="text-2xl font-bold text-blue-600">{testsCompleted}/3</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PerformanceScreen() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('performance');
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth(); // Get current user
  
  // State for all our data
  const [scoreData, setScoreData] = useState([]);
  const [detailedQuestionData, setDetailedQuestionData] = useState([]);
  const [weaknessesData, setWeaknessesData] = useState([]);
  const [studyHoursData, setStudyHoursData] = useState([]);
  const [skillRadarData, setSkillRadarData] = useState([]);
  const [comparativeData, setComparativeData] = useState([]);
  const [errorPatternsData, setErrorPatternsData] = useState([]);
  const [timeDistributionData, setTimeDistributionData] = useState([]);
  const [currentScore, setCurrentScore] = useState(0);
  const [scoreImprovement, setScoreImprovement] = useState(0);
  const [percentile, setPercentile] = useState(0);
  const [totalStudyTime, setTotalStudyTime] = useState(0);
  const [studyTimeLastWeek, setStudyTimeLastWeek] = useState(0);
  const [testsCompleted, setTestsCompleted] = useState(0);
  const [totalTests, setTotalTests] = useState(0);
  const [targetScore, setTargetScore] = useState(0);
  const [predictedScore, setPredictedScore] = useState(0);

  // Fetch data from Supabase
  useEffect(() => {
    if (!user?.id) return;
    
    // Set loading state
    setIsLoading(true);
    
    // Fetch all data in parallel
    Promise.all([
      fetchScoreData(),
      fetchDetailedQuestionData(),
      fetchWeaknessesData(),
      fetchStudyHoursData(),
      fetchSkillRadarData(),
      fetchComparativeData(),
      fetchErrorPatternsData(),
      fetchOverviewStats(),
    ]).finally(() => {
      setIsLoading(false);
    });
  }, [user]);

  // Function to fetch score data over time
  const fetchScoreData = async () => {
    // Get a user's test scores ordered by completion date
    const { data, error } = await supabase
      .from('profile_tests')
      .select(`
        id,
        score,
        completed_on,
        test_id,
        tests(test_name)
      `)
      .eq('profile_id', user.id)
      .not('completed_on', 'is', null)
      .order('completed_on', { ascending: true });
      
    if (error) {
      console.error('Error fetching score data:', error);
      return;
    }
    
    // Also fetch average scores for comparison
    const { data: avgData, error: avgError } = await supabase
      .rpc('get_average_test_scores_by_test_id');
      
    if (avgError) {
      console.error('Error fetching average scores:', avgError);
    }
    
    // Format the data for the chart
    const formattedData = data.map((item, index) => {
      const testId = item.test_id;
      const avgScore = avgData?.find(avg => avg.test_id === testId)?.avg_score || null;
      
      return {
        name: `Test ${index + 1}`,
        score: Number(item.score),
        avgScore: avgScore ? Number(avgScore) : null,
        date: item.completed_on ? new Date(item.completed_on).toISOString().split('T')[0] : '',
        testName: item.tests.test_name
      };
    });
    
    setScoreData(formattedData);
    
    // Set current score and improvement
    if (formattedData.length > 0) {
      const latestScore = formattedData[formattedData.length - 1].score;
      setCurrentScore(latestScore);
      
      if (formattedData.length > 1) {
        const previousScore = formattedData[formattedData.length - 2].score;
        setScoreImprovement(latestScore - previousScore);
      }

      // Calculate predicted score based on the last 3 tests if available
      if (formattedData.length >= 3) {
        const latestScores = formattedData.slice(-3).map(item => item.score);
        const avgRecentScore = latestScores.reduce((a, b) => a + b, 0) / latestScores.length;
        setPredictedScore(Math.round(avgRecentScore + 3)); // Simple prediction algorithm
      }
    }
  };

  // Function to fetch detailed question data
  const fetchDetailedQuestionData = async () => {
    // Get the latest completed test
    const { data: latestTest, error: testError } = await supabase
      .from('profile_tests')
      .select('id, test_id')
      .eq('profile_id', user.id)
      .not('completed_on', 'is', null)
      .order('completed_on', { ascending: false })
      .limit(1);
      
    if (testError || !latestTest?.length) {
      console.error('Error fetching latest test:', testError);
      return;
    }
    
    const latestTestId = latestTest[0].id;
    
    // Get question performance for the latest test
    // This would require a custom function or a complex query joining multiple tables
    const { data, error } = await supabase
      .rpc('get_question_performance_for_test', { 
        p_profile_test_id: latestTestId 
      });
      
    if (error) {
      console.error('Error fetching question data:', error);
      return;
    }
    
    // Format the data
    const formattedData = data.map((item, index) => ({
      id: index + 1,
      section: item.category || 'Unknown',
      type: item.question_type || 'Unknown',
      correct: item.is_correct,
      time: item.time_taken || 0,
      difficulty: item.level || 'Medium',
      date: new Date(item.completed_on).toISOString().split('T')[0]
    }));
    
    setDetailedQuestionData(formattedData);
    
    // Also set time distribution data based on this
    setTimeDistributionData(formattedData.map(q => ({
      id: q.id,
      time: q.time,
      correct: q.correct,
      type: q.type,
      section: q.section
    })));
  };

  // Function to fetch weaknesses data
  const fetchWeaknessesData = async () => {
    // Get performance by question type/category
    const { data, error } = await supabase
      .rpc('get_performance_by_question_type', { 
        p_profile_id: user.id 
      });
      
    if (error) {
      console.error('Error fetching weaknesses data:', error);
      return;
    }
    
    // Get average performance for comparison
    const { data: avgData, error: avgError } = await supabase
      .rpc('get_average_performance_by_question_type');
      
    if (avgError) {
      console.error('Error fetching average performance:', avgError);
    }
    
    // Find the 5 worst-performing areas
    const formattedData = data
      .map(item => {
        const avgPerf = avgData?.find(avg => avg.question_type === item.question_type)?.avg_score || 0;
        return {
          name: item.question_type,
          score: Math.round(item.correct_percentage * 100),
          avg: Math.round(avgPerf * 100)
        };
      })
      .sort((a, b) => a.score - b.score)
      .slice(0, 5);
    
    setWeaknessesData(formattedData);
  };

  // Function to fetch study hours data
  const fetchStudyHoursData = async () => {
    // Get study hours by week and corresponding score improvements
    const { data, error } = await supabase
      .rpc('get_study_hours_and_improvement', { 
        p_profile_id: user.id 
      });
      
    if (error) {
      console.error('Error fetching study hours data:', error);
      return;
    }
    
    // Format the data for the chart
    const formattedData = data.map((item, index) => ({
      date: `Week ${index + 1}`,
      hours: item.hours || 0,
      improvement: item.score_improvement || 0
    }));
    
    setStudyHoursData(formattedData);
    
    // Calculate total study time and last week's study time
    if (formattedData.length > 0) {
      const totalHours = formattedData.reduce((sum, item) => sum + item.hours, 0);
      setTotalStudyTime(totalHours);
      
      if (formattedData.length > 0) {
        const lastWeekHours = formattedData[formattedData.length - 1].hours;
        setStudyTimeLastWeek(lastWeekHours);
      }
    }
  };

  // Function to fetch skill radar data
  const fetchSkillRadarData = async () => {
    // Get performance by skill category
    const { data, error } = await supabase
      .rpc('get_performance_by_skill_category', { 
        p_profile_id: user.id 
      });
      
    if (error) {
      console.error('Error fetching skill radar data:', error);
      return;
    }
    
    // Format the data for the radar chart
    const formattedData = data.map(item => ({
      subject: item.category,
      A: Math.round(item.performance_score * 100),
      fullMark: 100
    }));
    
    setSkillRadarData(formattedData);
  };

  // Function to fetch comparative data
  const fetchComparativeData = async () => {
    // Get user's overall performance and by section
    const { data: userData, error: userError } = await supabase
      .rpc('get_user_performance_by_section', { 
        p_profile_id: user.id 
      });
      
    if (userError) {
      console.error('Error fetching user performance:', userError);
      return;
    }
    
    // Get average performance
    const { data: avgData, error: avgError } = await supabase
      .rpc('get_average_performance_by_section');
      
    if (avgError) {
      console.error('Error fetching average performance:', avgError);
    }
    
    // Get top performers' data
    const { data: topData, error: topError } = await supabase
      .rpc('get_top_performers_by_section');
      
    if (topError) {
      console.error('Error fetching top performers:', topError);
    }
    
    // Format the data for the comparison chart
    const formattedData = userData.map(item => {
      const category = item.section || 'Overall Score';
      const avgScore = avgData?.find(avg => avg.section === item.section)?.avg_score || 0;
      const topScore = topData?.find(top => top.section === item.section)?.top_score || 0;
      
      return {
        category,
        yourScore: Math.round(item.score),
        avgScore: Math.round(avgScore),
        topPerformers: Math.round(topScore)
      };
    });
    
    setComparativeData(formattedData);
    
    // Calculate percentile ranking based on overall score
    const overallUserScore = userData.find(item => item.section === 'Overall')?.score || 0;
    const { data: percentileData, error: percentileError } = await supabase
      .rpc('get_user_percentile', { 
        p_score: overallUserScore 
      });
      
    if (percentileError) {
      console.error('Error calculating percentile:', percentileError);
    } else if (percentileData) {
      setPercentile(Math.round(percentileData * 100));
    }
  };

  // Function to fetch error patterns data
  const fetchErrorPatternsData = async () => {
    // Get error patterns from recent tests
    const { data, error } = await supabase
      .rpc('get_error_patterns', { 
        p_profile_id: user.id 
      });
      
    if (error) {
      console.error('Error fetching error patterns:', error);
      return;
    }
    
    // Format the data for the pie chart
    const formattedData = data.map(item => ({
      name: item.error_type,
      count: item.count
    }));
    
    setErrorPatternsData(formattedData);
  };

  // Function to fetch overview stats
  const fetchOverviewStats = async () => {
    // Get tests completed and total tests
    const { data: testsData, error: testsError } = await supabase
      .from('profile_tests')
      .select('id, test_id')
      .eq('profile_id', user.id)
      .not('completed_on', 'is', null);
      
    if (testsError) {
      console.error('Error fetching tests completed:', testsError);
    } else {
      setTestsCompleted(testsData.length);
    }
    
    // Get total tests available
    const { data: totalTestsData, error: totalTestsError } = await supabase
      .from('tests')
      .select('id')
      .eq('category', 'LSAT'); // Assuming we want to filter by category
      
    if (totalTestsError) {
      console.error('Error fetching total tests:', totalTestsError);
    } else {
      setTotalTests(totalTestsData.length);
    }
    
    // Get target score from user profile
    const { data: profileData, error: profileError } = await supabase
      .from('profiles')
      .select('settings_personalized_recommendations, study_goals')
      .eq('id', user.id)
      .single();
      
    if (profileError) {
      console.error('Error fetching profile data:', profileError);
    } else if (profileData?.study_goals) {
      try {
        const goals = JSON.parse(profileData.study_goals);
        if (goals.target_score) {
          setTargetScore(goals.target_score);
        }
      } catch (e) {
        console.error('Error parsing study goals:', e);
      }
    }
  };

  // If loading, show loading state
  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen">Loading performance data...</div>;
  }

  // Calculate time distribution data if not set by fetchDetailedQuestionData
  const currentTimeDistributionData = timeDistributionData.length > 0 
    ? timeDistributionData 
    : detailedQuestionData.map(q => ({
        id: q.id,
        time: q.time,
        correct: q.correct,
        type: q.type,
        section: q.section
      }));

  // Check if the user has completed any tests
  const hasCompletedTests = testsCompleted > 0;

  // Colors for charts
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];
  
  const hasEnoughTestsForAnalytics = testsCompleted >= 3;
  const hasCompletedSomeTests = testsCompleted > 0;

  // Tab content definitions
  const tabContent = {
    performance: (
      <div className="space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow p-6 col-span-2">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800">Score Trajectory</h2>
              <div className="flex items-center text-sm text-gray-600">
                <div className="w-3 h-3 bg-blue-500 rounded-full mr-1"></div>
                <span>Your Score</span>
                <div className="w-3 h-3 bg-gray-400 rounded-full ml-3 mr-1"></div>
                <span>Average User</span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <ComposedChart data={scoreData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis domain={[140, 180]} />
                <Tooltip />
                <Legend />
                <Area type="monotone" dataKey="avgScore" fill="#E5E7EB" stroke="#9CA3AF" />
                <Line type="monotone" dataKey="score" stroke="#3B82F6" strokeWidth={3} dot={{ r: 6 }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Skill Analysis</h2>
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart outerRadius={90} data={skillRadarData}>
                <PolarGrid />
                <PolarAngleAxis dataKey="subject" />
                <PolarRadiusAxis angle={30} domain={[0, 100]} />
                <Radar name="Your Skills" dataKey="A" stroke="#8884d8" fill="#8884d8" fillOpacity={0.6} />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Areas for Improvement</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={weaknessesData}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <XAxis type="number" domain={[0, 100]} />
                <YAxis dataKey="name" type="category" width={100} />
                <CartesianGrid strokeDasharray="3 3" />
                <Tooltip />
                <Legend />
                <Bar dataKey="score" fill="#F87171" name="Your Score" />
                <Bar dataKey="avg" fill="#9CA3AF" name="Average" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-lg shadow p-6 col-span-2">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Answer Time vs. Correctness</h2>
            <ResponsiveContainer width="100%" height={300}>
              <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                <CartesianGrid />
                <XAxis type="number" dataKey="time" name="Time (seconds)" />
                <YAxis dataKey="id" name="Question" />
                <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                <Scatter 
                  name="Correct" 
                  data={currentTimeDistributionData.filter(q => q.correct)} 
                  fill="#10B981" 
                />
                <Scatter 
                  name="Incorrect" 
                  data={currentTimeDistributionData.filter(q => !q.correct)} 
                  fill="#F87171" 
                />
                <Legend />
              </ScatterChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    ),
    
    comparison: (
      <div className="space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="col-span-2 bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Performance Comparison</h2>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={comparativeData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="category" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="yourScore" name="Your Score" fill="#3B82F6" />
                <Bar dataKey="avgScore" name="Average Users" fill="#9CA3AF" />
                <Bar dataKey="topPerformers" name="Top 10% Users" fill="#10B981" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Your Percentile Ranking</h2>
            <div className="flex flex-col items-center justify-center h-64">
              <div className="relative w-48 h-48">
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-4xl font-bold text-blue-600">{percentile}%</span>
                </div>
                <svg viewBox="0 0 100 100" className="w-full h-full">
                  <circle 
                    cx="50" cy="50" r="45" 
                    fill="none" 
                    stroke="#E5E7EB" 
                    strokeWidth="10" 
                  />
                  <circle 
                    cx="50" cy="50" r="45" 
                    fill="none" 
                    stroke="#3B82F6" 
                    strokeWidth="10" 
                    strokeDasharray="282.7"
                    strokeDashoffset={282.7 * (1 - percentile / 100)} 
                    strokeLinecap="round"
                    transform="rotate(-90 50 50)" 
                  />
                </svg>
              </div>
              <p className="text-gray-600 mt-4 text-center">You're performing better than {percentile}% of all test takers in our database</p>
            </div>
          </div>
        </div>
          
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Study Hours vs. Score Improvement</h2>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={studyHoursData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis yAxisId="left" orientation="left" stroke="#3B82F6" />
              <YAxis yAxisId="right" orientation="right" stroke="#10B981" />
              <Tooltip />
              <Legend />
              <Bar yAxisId="left" dataKey="hours" fill="#3B82F6" name="Study Hours" />
              <Line yAxisId="right" type="monotone" dataKey="improvement" stroke="#10B981" name="Point Improvement" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    ),
    
    diagnostics: (
      <div className="space-y-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Error Pattern Analysis</h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={errorPatternsData}
                  cx="50%"
                  cy="50%"
                  labelLine={true}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                  nameKey="name"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  {errorPatternsData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Question Difficulty Impact</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={[
                {difficulty: 'Easy', correct: detailedQuestionData.filter(q => q.difficulty === 'Easy' && q.correct).length, incorrect: detailedQuestionData.filter(q => q.difficulty === 'Easy' && !q.correct).length},
                {difficulty: 'Medium', correct: detailedQuestionData.filter(q => q.difficulty === 'Medium' && q.correct).length, incorrect: detailedQuestionData.filter(q => q.difficulty === 'Medium' && !q.correct).length},
                {difficulty: 'Hard', correct: detailedQuestionData.filter(q => q.difficulty === 'Hard' && q.correct).length, incorrect: detailedQuestionData.filter(q => q.difficulty === 'Hard' && !q.correct).length},
              ]}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="difficulty" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="correct" stackId="a" fill="#10B981" name="Correct" />
                <Bar dataKey="incorrect" stackId="a" fill="#F87171" name="Incorrect" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Recent Question Analysis</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Section</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Result</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time (sec)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Difficulty</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {detailedQuestionData.map((question) => (
                  <tr key={question.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{question.id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{question.section}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{question.type}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${question.correct ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {question.correct ? 'Correct' : 'Incorrect'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{question.time}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{question.difficulty}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Time Management Analysis</h2>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm font-medium text-gray-700">Logical Reasoning</span>
                  <span className="text-sm text-gray-600">35:11 avg / 35:00 target</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div className="bg-yellow-400 h-2.5 rounded-full" style={{ width: '89%' }}></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm font-medium text-gray-700">Reading Comprehension</span>
                  <span className="text-sm text-gray-600">33:24 avg / 35:00 target</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div className="bg-green-500 h-2.5 rounded-full" style={{ width: '95%' }}></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm font-medium text-gray-700">Analytical Reasoning</span>
                  <span className="text-sm text-gray-600">39:45 avg / 35:00 target</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div className="bg-red-500 h-2.5 rounded-full" style={{ width: '114%' }}></div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">Predicted LSAT Score</h2>
            <div className="flex items-center justify-center h-48">
              <div className="text-center">
                <div className="text-6xl font-bold text-blue-600">{predictedScore}</div>
                <p className="text-gray-500 mt-2">Based on your recent performance</p>
                <div className="flex items-center justify-center mt-4">
                  <div className="px-4 py-2 bg-blue-50 rounded-lg">
                    <div className="flex items-center">
                      <span className="text-sm text-gray-600 mr-2">Score Range:</span>
                      <span className="font-medium text-gray-800">{predictedScore - 2} - {predictedScore + 2}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  };

  // No tests completed view with SVG icon
  const NoTestsView = () => (
    <div className="bg-white rounded-lg shadow p-8 text-center">
      <div className="flex justify-center mb-6">
        <svg width="200" height="200" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
          {/* Clipboard background */}
          <rect x="40" y="15" width="120" height="170" rx="6" fill="#F3F4F6" stroke="#D1D5DB" strokeWidth="2" />
          <rect x="50" y="25" width="100" height="150" rx="2" fill="white" stroke="#D1D5DB" strokeWidth="2" />
          
          {/* Clipboard top */}
          <rect x="75" y="5" width="50" height="20" rx="6" fill="#3B82F6" stroke="#2563EB" strokeWidth="2" />
          
          {/* Test paper with lines */}
          <line x1="60" y1="45" x2="140" y2="45" stroke="#E5E7EB" strokeWidth="2" />
          <line x1="60" y1="65" x2="140" y2="65" stroke="#E5E7EB" strokeWidth="2" />
          <line x1="60" y1="85" x2="140" y2="85" stroke="#E5E7EB" strokeWidth="2" />
          <line x1="60" y1="105" x2="140" y2="105" stroke="#E5E7EB" strokeWidth="2" />
          <line x1="60" y1="125" x2="140" y2="125" stroke="#E5E7EB" strokeWidth="2" />
          <line x1="60" y1="145" x2="140" y2="145" stroke="#E5E7EB" strokeWidth="2" />
          
          {/* Pencil */}
          <polygon points="145,150 165,135 175,145 155,160" fill="#FBBF24" stroke="#D97706" strokeWidth="1.5" />
          <polygon points="155,160 145,150 140,165 155,160" fill="#9CA3AF" stroke="#6B7280" strokeWidth="1.5" />
          <line x1="165" y1="135" x2="175" y2="145" stroke="#6B7280" strokeWidth="1" />
          
          {/* Chart icon */}
          <rect x="75" y="50" width="50" height="40" rx="2" fill="#EFF6FF" stroke="#3B82F6" strokeWidth="1.5" />
          <line x1="85" y1="70" x2="85" y2="80" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" />
          <line x1="95" y1="65" x2="95" y2="80" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" />
          <line x1="105" y1="60" x2="105" y2="80" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" />
          <line x1="115" y1="75" x2="115" y2="80" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" />
          
          {/* Alert circle */}
          <circle cx="150" cy="40" r="18" fill="#FEF2F2" stroke="#EF4444" strokeWidth="1.5" />
          <path d="M150 30v14" stroke="#EF4444" strokeWidth="2.5" strokeLinecap="round" />
          <circle cx="150" cy="50" r="2" fill="#EF4444" />
        </svg>
      </div>
      <h2 className="text-2xl font-bold text-gray-800 mb-3">No Test Data Available</h2>
      <p className="text-gray-600 mb-6 max-w-md mx-auto">
        You need to complete at least one test before your performance analytics can be displayed. Taking tests will help us provide personalized insights and track your progress over time.
      </p>
      <div className="inline-flex items-center">
          <a 
            onClick={(e) => { e.preventDefault(); navigate('/order') }}
            href="/order"
            className="px-8 py-4 rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium text-lg flex items-center justify-center hover:opacity-90 transition-opacity shadow-lg"
          >
            Start a Test
          </a>
      </div>
    </div>
  );

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="py-12 px-4 sm:px-6 lg:px-8 bg-linear-to-r from-indigo-600 to-purple-600 mb-8">
        <div className="max-w-7xl mx-auto">
          <div className="md:flex md:items-center md:justify-between">
            <div className="md:w-2/3">
              <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Your Performance</h1>
              <p className="text-xl text-indigo-100">
                Detailed insights to optimize your LSAT preparation strategy
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {!hasCompletedSomeTests ? (
          <NoTestsView />
        ) : !hasEnoughTestsForAnalytics ? (
          <NotEnoughTestsView testsCompleted={testsCompleted} currentScore={currentScore} totalTests={totalTests} />
        ) : (
          <>
            {/* Stats Overview Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg shadow text-white p-6">
                <div className="flex justify-between">
                  <div>
                    <p className="text-blue-100">Current Score</p>
                    <p className="text-3xl font-bold mt-1">{currentScore}</p>
                  </div>
                  <div className="flex items-start">
                    {scoreImprovement > 0 ? (
                      <>
                        <ArrowUp className="text-blue-200" size={20} />
                        <span className="ml-1 text-blue-100">+{scoreImprovement}</span>
                      </>
                    ) : scoreImprovement < 0 ? (
                      <>
                        <ArrowDown className="text-blue-200" size={20} />
                        <span className="ml-1 text-blue-100">{scoreImprovement}</span>
                      </>
                    ) : (
                      <span className="ml-1 text-blue-100">No change</span>
                    )}
                  </div>
                </div>
                <div className="mt-4 text-sm text-blue-100">
                  Top {100 - percentile}% of test takers
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between">
                  <div>
                    <p className="text-gray-500">Study Time</p>
                    <p className="text-3xl font-bold text-gray-800 mt-1">{totalStudyTime}<span className="text-lg font-normal"> hrs</span></p>
                  </div>
                  <div className="p-2 bg-blue-100 h-10 w-10 rounded-full flex items-center justify-center">
                    <Clock className="text-blue-600" size={20} />
                  </div>
                </div>
                <div className="mt-4 text-sm text-gray-500">
                  <span className="text-blue-600 font-medium">+{studyTimeLastWeek} hours</span> last week
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between">
                  <div>
                    <p className="text-gray-500">Tests Completed</p>
                    <p className="text-3xl font-bold text-gray-800 mt-1">{testsCompleted}<span className="text-lg font-normal">/{totalTests}</span></p>
                  </div>
                  <div className="p-2 bg-green-100 h-10 w-10 rounded-full flex items-center justify-center">
                    <BookOpen className="text-green-600" size={20} />
                  </div>
                </div>
                <div className="mt-4 text-sm text-gray-500">
                  <span className="text-green-600 font-medium">Program:</span> {Math.round((testsCompleted / totalTests) * 100)}% complete
                </div>
              </div>
              
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between">
                  <div>
                    <p className="text-gray-500">Target Score</p>
                    <p className="text-3xl font-bold text-gray-800 mt-1">{targetScore}+</p>
                  </div>
                  <div className="p-2 bg-purple-100 h-10 w-10 rounded-full flex items-center justify-center">
                    <Target className="text-purple-600" size={20} />
                  </div>
                </div>
                <div className="mt-4 text-sm text-gray-500">
                  <span className="text-purple-600 font-medium">{targetScore - currentScore} points</span> to reach target
                </div>
              </div>
            </div>

            {/* Tab navigation */}
            <div className="bg-white rounded-lg shadow mb-6">
              <div className="border-b border-gray-200">
                <nav className="flex -mb-px">
                  <button
                    onClick={() => setActiveTab('performance')}
                    className={`py-4 px-6 font-medium text-sm flex items-center border-b-2 ${
                      activeTab === 'performance'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <BarChart2 size={18} className="mr-2" />
                    Performance Analysis
                  </button>
                  <button
                    onClick={() => setActiveTab('comparison')}
                    className={`py-4 px-6 font-medium text-sm flex items-center border-b-2 ${
                      activeTab === 'comparison'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Users size={18} className="mr-2" />
                    Comparative Stats
                  </button>
                  <button
                    onClick={() => setActiveTab('diagnostics')}
                    className={`py-4 px-6 font-medium text-sm flex items-center border-b-2 ${
                      activeTab === 'diagnostics'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Brain size={18} className="mr-2" />
                    Diagnostic Insights
                  </button>
                </nav>
              </div>
            </div>

            {/* Filter bar */}
            <div className="bg-white rounded-lg shadow p-4 mb-6 flex flex-wrap items-center gap-4">
              <div className="flex items-center">
                <Filter size={18} className="text-gray-500 mr-2" />
                <span className="text-gray-700 font-medium">Filters:</span>
              </div>
              
              <div className="flex items-center bg-gray-100 rounded-md px-3 py-1.5">
                <Calendar size={16} className="text-gray-500 mr-2" />
                <span className="text-sm text-gray-700">Last 3 months</span>
                <ChevronDown size={16} className="text-gray-500 ml-2" />
              </div>
              
              <div className="flex items-center bg-gray-100 rounded-md px-3 py-1.5">
                <BookOpen size={16} className="text-gray-500 mr-2" />
                <span className="text-sm text-gray-700">All tests</span>
                <ChevronDown size={16} className="text-gray-500 ml-2" />
              </div>
              
              <div className="flex items-center bg-gray-100 rounded-md px-3 py-1.5">
                <Zap size={16} className="text-gray-500 mr-2" />
                <span className="text-sm text-gray-700">All difficulty levels</span>
                <ChevronDown size={16} className="text-gray-500 ml-2" />
              </div>
              
              <button className="ml-auto bg-blue-100 text-blue-700 px-4 py-1.5 rounded-md text-sm font-medium">
                Apply Filters
              </button>
            </div>

            {/* Tab content */}
            {tabContent[activeTab]}
            
            {/* Recommendations */}
            <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                <Brain size={20} className="text-blue-600 mr-2" />
                AI-Generated Study Recommendations
              </h2>
              <div className="space-y-4">
                {weaknessesData.length > 0 && (
                  <div className="bg-white rounded-md p-4 border-l-4 border-yellow-500">
                    <h3 className="font-medium text-gray-800">Focus on {weaknessesData[0].name}</h3>
                    <p className="text-gray-600 mt-1">Your performance on {weaknessesData[0].name.toLowerCase()} is consistently lower. Practice at least 5 questions of this type this week with detailed analysis.</p>
                  </div>
                )}
                {detailedQuestionData.some(q => q.section === 'AR' && !q.correct) && (
                  <div className="bg-white rounded-md p-4 border-l-4 border-blue-500">
                    <h3 className="font-medium text-gray-800">Improve Time Management</h3>
                    <p className="text-gray-600 mt-1">You're consistently spending too much time on Analytical Reasoning. Try timed drills focusing only on this section to improve your pacing.</p>
                  </div>
                )}
                {skillRadarData.length > 0 && (
                  <div className="bg-white rounded-md p-4 border-l-4 border-green-500">
                    <h3 className="font-medium text-gray-800">Maintain {skillRadarData.reduce((prev, current) => (prev.A > current.A) ? prev : current).subject} Strength</h3>
                    <p className="text-gray-600 mt-1">Your {skillRadarData.reduce((prev, current) => (prev.A > current.A) ? prev : current).subject} section is your strongest area. Keep this up with regular practice.</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
